// Overview cockpit: per-inverter status grid (8 mini instrument widgets) +
// intra-fleet spread band over the daylight window (min–max + median).
import { useMemo } from 'react';
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  ComposedChart, Area, Line, Label,
} from 'recharts';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { useInverterLatest, useInverterDay } from '@/scada/useInverters';

const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
const devColor = (pct: number | null, med: number | null, offline?: boolean) => {
  if (offline) return scada.state.offline;
  if (pct == null || med == null) return scada.textMuted;
  const d = pct - med;
  if (d >= -3) return scada.state.ok;
  if (d >= -10) return scada.state.warning;
  return scada.state.alarm;
};

// Loading placeholder that keeps the card height stable (no layout shift).
function Skeleton({ height }: { height: number }) {
  return <div className="animate-pulse" style={{ height, background: scada.panel2, borderRadius: 8 }} />;
}

// ── Per-inverter status grid: 8 mini instrument widgets ──────────────────────
export function InverterStrip({ assetId }: { assetId?: string }) {
  const { data, meta, err } = useInverterLatest(assetId);
  const mode = meta?.mode;
  const perInverter = mode === 'per_inverter';
  const med = useMemo(() => median((data ?? []).map((d) => d.percentOfNominal).filter((x): x is number => x != null)), [data]);
  const working = data ? data.filter((d) => d.status === 'ok').length : 0;

  return (
    <SectionCard
      title="Технічний стан інверторів"
      subtitle={perInverter ? 'Кожен інвертор: потужність, % від номіналу, відхилення від медіани групи' : 'Стан інверторного парку станції'}
      right={perInverter && data && med != null ? <span style={{ color: scada.textMuted, fontSize: 11 }}>{working}/{data.length} у роботі · медіана {med.toFixed(0)}%</span> : undefined}
    >
      {!data && !err && <Skeleton height={110} />}
      {(err || mode === 'none') && !data && (
        <span style={{ color: scada.textMuted, fontSize: 12 }}>Немає даних по інверторах для цієї станції.</span>
      )}

      {/* Aggregate (SMA): group banner above a synthesized fleet grid */}
      {data && mode === 'aggregate' && meta?.groupName && (
        <div className="flex items-center gap-2 flex-wrap" style={{ background: `${scada.state.curtailed}12`, border: `1px solid ${scada.state.curtailed}33`, borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          <i className="ri-server-line" style={{ color: scada.state.curtailed }} />
          <span style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{meta.groupName}</span>
          <span style={{ color: scada.textMuted, fontSize: 12 }}>· {meta.availableInverters ?? '—'}/{meta.expectedInverters ?? '—'} у роботі · сумарно {meta.plantPacKw != null ? (meta.plantPacKw / 1000).toFixed(2) : '—'} МВт</span>
          <span style={{ color: scada.textMuted, fontSize: 11, marginLeft: 'auto' }}>орієнтовні значення по інверторах (розподіл сумарного показника)</span>
        </div>
      )}

      {data && (perInverter || mode === 'aggregate') && (
      <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))' }}>
        {data.map((inv) => {
          const c = devColor(inv.percentOfNominal, med, inv.status === 'offline');
          const pct = Math.max(0, Math.min(100, inv.percentOfNominal ?? 0));
          const dev = med != null && inv.percentOfNominal != null ? inv.percentOfNominal - med : null;
          return (
            <div key={inv.unitId} style={{ background: scada.panel2, border: `1px solid ${scada.border}`, borderTop: `3px solid ${c}`, borderRadius: 10, padding: '10px 11px' }}>
              <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                <span style={{ color: scada.text, fontSize: 12, fontWeight: 600 }}>INV-{String(inv.unitId).padStart(2, '0')}</span>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: c, display: 'inline-block' }} />
              </div>
              <div className="flex items-end gap-2.5">
                {/* vertical bar instrument */}
                <div style={{ position: 'relative', width: 18, height: 64, background: scada.bg, borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: `${pct}%`, background: c, borderRadius: '0 0 4px 4px' }} />
                  {/* 100% tick */}
                  <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 1, background: scada.borderStrong }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: scada.text, fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{(inv.pacKw ?? 0).toFixed(1)}</div>
                  <div style={{ color: scada.textDim, fontSize: 10 }}>кВт</div>
                  <div style={{ color: c, fontSize: 13, fontWeight: 600, marginTop: 4 }}>{inv.percentOfNominal != null ? `${inv.percentOfNominal}%` : '—'}</div>
                  <div style={{ color: dev == null ? scada.textMuted : dev >= -3 ? scada.textMuted : c, fontSize: 10 }}>
                    {inv.synthetic
                      ? (inv.status === 'offline' ? 'офлайн' : 'оцінка')
                      : dev == null ? (inv.status === 'offline' ? 'офлайн' : '') : `${dev > 0 ? '+' : ''}${dev.toFixed(0)}% до мед.`}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </SectionCard>
  );
}

// ── Intra-fleet spread over the day: min–max band + median (daylight only) ───
export function StationDayChart({ assetId }: { assetId?: string }) {
  const { data: latest, meta } = useInverterLatest(assetId);
  const { series, err } = useInverterDay(assetId);
  const nominalKw = latest?.[0]?.nominalKw ?? null;
  const mode = meta?.mode;

  // Station total power over the day (kW) — used for aggregate stations.
  const powerDay = useMemo(() => {
    if (!series) return [];
    const byTime = new Map<string, number>();
    for (const s of series) for (const p of s.points) {
      if (p.pac == null) continue;
      byTime.set(p.t, (byTime.get(p.t) ?? 0) + p.pac);
    }
    const all = [...byTime.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([t, kw]) => ({ t, kw: Math.round(kw) }));
    const lo = all.findIndex((p) => p.kw > 1);
    const hi = all.length - 1 - [...all].reverse().findIndex((p) => p.kw > 1);
    return lo === -1 ? [] : all.slice(lo, hi + 1);
  }, [series]);

  const band = useMemo(() => {
    if (!series || !nominalKw) return [];
    const byTime = new Map<string, number[]>();
    for (const s of series) for (const p of s.points) {
      if (p.pac == null) continue;
      const pct = (p.pac / nominalKw) * 100;
      if (!byTime.has(p.t)) byTime.set(p.t, []);
      byTime.get(p.t)!.push(pct);
    }
    const all = [...byTime.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([t, arr]) => {
      const mn = Math.min(...arr), mx = Math.max(...arr);
      return { t, min: mn, range: mx - mn, max: mx, med: median(arr) ?? 0, spread: mx - mn };
    });
    const lo = all.findIndex((p) => p.max > 1);
    const hi = all.length - 1 - [...all].reverse().findIndex((p) => p.max > 1);
    return lo === -1 ? [] : all.slice(lo, hi + 1);
  }, [series, nominalKw]);

  const loading = !series && !err;
  const aggregate = mode === 'aggregate';
  const maxSpread = band.length ? Math.max(...band.map((b) => b.spread)) : 0;
  const hasContent = aggregate ? powerDay.length > 0 : band.length > 0;

  return (
    <SectionCard
      title={aggregate ? 'Потужність станції за добу' : 'Рівномірність роботи інверторів за добу'}
      subtitle={aggregate
        ? 'Сумарна потужність станції (агрегований облік — розподіл по інверторах недоступний)'
        : 'Зелена лінія — типовий (середній) інвертор; смуга — розрив між найсильнішим і найслабшим. Вузька смуга = усі працюють однаково, ширша = якийсь інвертор відстає.'}
      right={!aggregate && band.length ? <span style={{ color: maxSpread > 15 ? scada.state.warning : scada.textMuted, fontSize: 11 }}>макс. розрив {maxSpread.toFixed(0)}%</span> : undefined}
    >
      {loading && <Skeleton height={230} />}
      {!loading && !hasContent && <span style={{ color: scada.textMuted, fontSize: 12 }}>Немає даних за сьогодні.</span>}

      {/* Aggregate: station total power (kW) area chart */}
      {aggregate && powerDay.length > 0 && (
        <div style={{ width: '100%', height: 230 }}>
          <ResponsiveContainer>
            <ComposedChart data={powerDay} margin={{ top: 8, right: 12, bottom: 18, left: 4 }}>
              <defs>
                <linearGradient id="stnpg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={scada.state.ok} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={scada.state.ok} stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={scada.border} strokeDasharray="3 3" />
              <XAxis dataKey="t" tick={{ fill: scada.textDim, fontSize: 10 }} stroke={scada.border} minTickGap={36} tickLine={false}>
                <Label value="Час доби" position="bottom" offset={4} fill={scada.textMuted} fontSize={11} />
              </XAxis>
              <YAxis tick={{ fill: scada.textDim, fontSize: 11 }} stroke={scada.border} tickLine={false} tickFormatter={(v) => `${v}`} width={48}>
                <Label value="кВт" angle={-90} position="insideLeft" fill={scada.textMuted} fontSize={11} style={{ textAnchor: 'middle' }} />
              </YAxis>
              <Tooltip contentStyle={{ background: scada.panel, border: `1px solid ${scada.border}`, borderRadius: 8, fontSize: 12 }} labelStyle={{ color: scada.text }}
                formatter={(v: any) => [`${v} кВт`, 'Потужність']} />
              <Area type="monotone" dataKey="kw" stroke={scada.state.ok} strokeWidth={2} fill="url(#stnpg)" isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {!aggregate && band.length > 0 && (
      <div style={{ width: '100%', height: 230 }}>
        <ResponsiveContainer>
          <ComposedChart data={band} margin={{ top: 8, right: 12, bottom: 18, left: 4 }}>
            <CartesianGrid stroke={scada.border} strokeDasharray="3 3" />
            <XAxis dataKey="t" tick={{ fill: scada.textDim, fontSize: 10 }} stroke={scada.border} minTickGap={36} tickLine={false}>
              <Label value="Час доби" position="bottom" offset={4} fill={scada.textMuted} fontSize={11} />
            </XAxis>
            <YAxis tick={{ fill: scada.textDim, fontSize: 11 }} stroke={scada.border} tickLine={false} tickFormatter={(v) => `${v}%`} width={42}>
              <Label value="% від номіналу" angle={-90} position="insideLeft" fill={scada.textMuted} fontSize={11} style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip
              contentStyle={{ background: scada.panel, border: `1px solid ${scada.border}`, borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: scada.text }}
              formatter={(_v: any, _n: any, p: any) => {
                const d = p.payload;
                return [`найслабший ${d.min.toFixed(0)}%  ·  типовий ${d.med.toFixed(0)}%  ·  найсильніший ${d.max.toFixed(0)}%`, 'Інвертори'];
              }} />
            <Area dataKey="min" stackId="band" stroke="none" fill="transparent" isAnimationActive={false} />
            <Area dataKey="range" stackId="band" stroke="none" fill={`${scada.state.curtailed}33`} isAnimationActive={false} name="Розкид" />
            <Line dataKey="med" stroke={scada.state.ok} strokeWidth={2} dot={false} name="Медіана" isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      )}
    </SectionCard>
  );
}
